import { useCallback, useEffect, useMemo, useState } from "react";

export const COMMUNICATIONS_TABLE_ROWS_PER_PAGE_OPTIONS = [
  5, 10, 25, 50,
] as const;

export interface UseTablePaginationOptions {
  /** Itens por página inicial (padrão do FluxZap: 10). */
  defaultRowsPerPage?: number;
}

export interface UseTablePaginationResult<T> {
  page: number;
  rowsPerPage: number;
  totalCount: number;
  paginatedItems: T[];
  handlePageChange: (_: unknown, newPage: number) => void;
  handleRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Paginação client-side no padrão de `PatientSelectionList` / `TutorSelectionList`.
 * `resetKey` deve mudar quando filtros ou lista base mudam (volta à página 0).
 */
export function useTablePagination<T>(
  items: readonly T[],
  resetKey?: string | number,
  options?: UseTablePaginationOptions,
): UseTablePaginationResult<T> {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    options?.defaultRowsPerPage ?? 5,
  );

  const totalCount = items.length;

  useEffect(() => {
    setPage(0);
  }, [resetKey]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, rowsPerPage, totalCount]);

  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage;
    return items.slice(start, start + rowsPerPage);
  }, [items, page, rowsPerPage]);

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  return {
    page,
    rowsPerPage,
    totalCount,
    paginatedItems,
    handlePageChange,
    handleRowsPerPageChange,
  };
}
